// ── Each operator's own top-menu order (Calem, report a0436e86) ──────────────────────────────────
// "Allow the user to change the menu blocks around. In other words give them a numbering system where the user
// can nominate that they want: Active and Closed Jobs block, Filing block, Quotations block etc. However they
// would like to reorder the top menu." — and on the 2nd opening: "Filing and jobs do not need to be pinned
// together or to the right-hand side. If the user prefers left, they can perhaps use numbering like L1, L2, M
// (for middle: middle 1, middle 2), and R1, R2, R3, all numbering running from left to right."
// So every block of the top menu carries a SLOT: a side — L(eft), M(iddle) or R(ight) — and a number, 1 to 4.
// The row is then three runs, read left to right: the L blocks from the left-hand end, the M blocks in the
// middle, the R blocks from the right-hand end, each run in its numbers' order. Nothing is pinned any more —
// Document and Jobs sit at the right because that is the slot they are given below, and either can be numbered
// into the left run instead.
// The slots below are the row exactly as it stands today, so a login that has never chosen sees the bar it
// always saw: Email / Contacts / Calendar, Quotation…Approvals, Petty Cash and Bank along the left (L1…L4),
// Document and Jobs at the right-hand end (R1, R2) — with Accounting, added later (report 362b5a61, one
// button since eb29e199), in the middle run at M1.
// The choice is kept per user on the server (routes/menuOrder.ts, on /api/me as `menuOrder`, no schema change),
// so the first paint is already in that person's order, and it is made in the ≡ logo menu under Default page
// (components/Portal). A block the login cannot see is simply not drawn, and its slot is left alone.

/** The three runs of the row, left to right. */
export const MENU_SIDES = ['L', 'M', 'R'] as const;
export type MenuSide = typeof MENU_SIDES[number];
/** How many slots each run offers — L1…L4, M1…M4, R1…R4, twelve being more than the blocks can need. */
export const MENU_SLOT_NUMBERS = [1, 2, 3, 4] as const;
/** A slot as it is stored and shown: the side's letter and the number, Calem's own numbering. */
export const MENU_SLOT_RE = /^[LMR][1-4]$/;

// The blocks, in the order they stand in with nobody's choice applied — which is also how a tie is broken
// (two blocks given the same slot keep this order between them). `label` is the i18n key the chooser names the
// block by: a block of several tabs is named by its first and last, joined by a dash, so the name stands on one
// line in the chooser's column (5th opening) — the boxes on the bar keep their own tabs' words. The boxes'
// colours live in src/tabPalette.ts (MENU_BLOCK_BOX), keyed by the same `key`.
export const MENU_BLOCKS: { key: string; label: string; slot: string }[] = [
  { key: 'email_cal', label: 'mo_blk_email_cal', slot: 'L1' },
  { key: 'peak', label: 'mo_blk_peak', slot: 'L2' },
  { key: 'petty', label: 'mo_blk_petty', slot: 'L3' },
  { key: 'bank', label: 'mo_blk_bank', slot: 'L4' },
  // The firm's own money (Calem, report 362b5a61): the red box, four bubbles then, ONE button reading
  // "Accounting" since report eb29e199. It is the SEVENTH block, and the left run is already full at L4,
  // so its default slot is M1 — the middle run, which draws it after Bank and before the right-hand
  // Document and Jobs, where a money block belongs. Nothing was moved to make room: Document and Jobs
  // keep R1 / R2 and stay pinned to the right-hand end, and anyone who would rather have it elsewhere
  // renumbers it in the ≡ menu like any other block. Its key is unchanged, so a slot already saved for
  // it still applies.
  { key: 'expense', label: 'mo_blk_expense', slot: 'M1' },
  { key: 'document', label: 'mo_blk_document', slot: 'R1' },
  { key: 'jobs', label: 'mo_blk_jobs', slot: 'R2' },
];

/** The slot a block sits in for this login: the one they chose, or its default. An unreadable or unknown saved
 *  value is read as no choice at all, so the bar falls back to the order above rather than losing a box. */
export function menuSlotOf(key: string, order?: Record<string, string>): string {
  const saved = order?.[key];
  if (saved && MENU_SLOT_RE.test(saved)) return saved;
  return MENU_BLOCKS.find(b => b.key === key)?.slot || 'L1';
}

/** The blocks of one run, in their numbers' order. `blocks` must come in MENU_BLOCKS' order: the sort is stable,
 *  so two blocks on the same number keep the default order between them. */
export function menuRunOf<T extends { key: string }>(blocks: T[], side: MenuSide, order?: Record<string, string>): T[] {
  return blocks
    .filter(b => menuSlotOf(b.key, order).startsWith(side))
    .sort((a, b) => Number(menuSlotOf(a.key, order).slice(1)) - Number(menuSlotOf(b.key, order).slice(1)));
}

/** The i18n key a slot is offered under in the chooser's select — 'L{{n}}' / 'M{{n}}' / 'R{{n}}'. */
export const menuSlotLabelKey = (side: MenuSide): string =>
  side === 'L' ? 'mo_slot_left' : side === 'M' ? 'mo_slot_middle' : 'mo_slot_right';

/** The i18n key a run's own name is shown under, as the select's heading for its four slots. */
export const menuSideLabelKey = (side: MenuSide): string =>
  side === 'L' ? 'mo_left' : side === 'M' ? 'mo_middle' : 'mo_right';
